/**
 * <AuthLayout> — the split-screen shell shared by every auth screen (login,
 * OTP, forgot, reset, invite) across all three apps.
 *
 *   ┌────────────────────────┬──────────────────────────────────┐
 *   │            [lang][☾]   │                                  │
 *   │   ┌────────────────┐   │   AuthBrandCanvas                │
 *   │   │  wordmark /    │   │   (office photo under a navy     │
 *   │   │  tenant logo   │   │    overlay, the LinkHR wordmark, │
 *   │   │  form column   │   │    a headline + three stats,     │
 *   │   │  © caption     │   │    the vendor lockup)            │
 *   │   └────────────────┘   │   hidden < md                    │
 *   └────────────────────────┴──────────────────────────────────┘
 *
 * - The form column sits at the inline-start (first in DOM) on the page paper;
 *   the canvas takes the inline-end 3/5 on `md+`. Logical properties only, so
 *   the split mirrors in RTL exactly like the design frame.
 * - Forms keep ownership of their own <h1> + aria-labelledby + per-state
 *   headings; the shell only provides chrome, so no heading/a11y wiring
 *   changes when a form swaps state.
 * - The brand header above the form shows the tenant logo when one is
 *   uploaded, else the neutral LinkHR wordmark — on every viewport, so phones
 *   (no canvas) still feel branded.
 *
 * Back-compat: `splash` still overrides the canvas; `branding` (from
 * `useAuthBranding`) drives the tenant logo + name + the runtime tenant color.
 */
import { useState, type ReactNode, type JSX } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "@linkhr/i18n";
import { AuthBrandCanvas } from "./AuthBrandCanvas.js";
import { AuthControls } from "./AuthControls.js";
import { LbsLogo } from "./brand/LbsLogo.js";
import { LinkHrWordmark } from "./brand/LinkHrWordmark.js";
import type { AuthBranding } from "./useAuthBranding.js";

export interface AuthLayoutProps {
  children: ReactNode;
  /** Tenant branding for the header + canvas. Absent → neutral wordmark. */
  branding?: AuthBranding;
  /** Legacy artwork override — replaces the brand canvas when provided. */
  splash?: ReactNode;
  /** Whose sign-in this is. Default Link HR. */
  product?: "hr" | "accounting";
}

export function AuthLayout({
  children,
  branding,
  splash,
  product = "hr",
}: AuthLayoutProps): JSX.Element {
  const { t } = useTranslation("auth");
  // A tenant logo URL can point at a purged storage object (dev resets, stale
  // uploads); a broken plate must fall back to the wordmark, never show the
  // browser's broken-image glyph. Keyed by URL so a LATER valid logo is never
  // hidden by an old failure.
  const [brokenLogoSrc, setBrokenLogoSrc] = useState<string | null>(null);
  const hasLogo = branding?.hasTenantLogo === true && branding.logoSrc !== brokenLogoSrc;
  const tenantName = branding?.tenantName ?? "";
  const year = formatNumber(new Date().getFullYear(), undefined, { useGrouping: false });

  return (
    <div className="auth-shell bg-surface grid min-h-screen grid-cols-1 md:grid-cols-[2fr_3fr]">
      {/* Form column — inline-start. */}
      <div className="relative flex items-center justify-center p-lg md:p-xl">
        {/* Above the column below: its entrance animation leaves a transform,
            which stacks it over this corner once the column is tall enough to
            reach the top, and the buttons stop taking clicks. */}
        <div className="absolute top-md end-md z-10">
          <AuthControls />
        </div>

        <div className="auth-anim-rise flex w-full max-w-md flex-col gap-lg">
          {/* Brand header — reserved min-height so the form doesn't shift when
              the tenant name resolves. */}
          <div className="flex min-h-[4rem] flex-col items-center justify-center gap-sm">
            {hasLogo ? (
              <img
                key={branding?.logoSrc}
                src={branding?.logoSrc}
                alt={tenantName.length > 0 ? tenantName : "Link HR"}
                height={48}
                className="h-12 w-auto max-w-[11rem] object-contain"
                onError={() => setBrokenLogoSrc(branding?.logoSrc ?? null)}
              />
            ) : product === "accounting" ? (
              <>
                <LbsLogo height={32} />
                <p className="text-default text-lg font-semibold">{t("brand.accounting_name")}</p>
              </>
            ) : (
              <LinkHrWordmark height={32} />
            )}
            {hasLogo && tenantName.length > 0 && (
              <p className="text-default text-lg font-semibold">{tenantName}</p>
            )}
          </div>

          {/* Form body — reserved min-height so multi-state routes (login↔OTP,
              invite loading/ready/terminal) don't shift the column. */}
          <div className="flex min-h-[20rem] flex-col gap-md">{children}</div>

          <p className="text-muted text-caption text-center">{t("brand.copyright", { year })}</p>
        </div>
      </div>

      {/* Brand canvas — inline-end, desktop only. */}
      <div className="hidden md:block">
        {splash ?? <AuthBrandCanvas branding={branding} product={product} />}
      </div>
    </div>
  );
}
